import { useState } from "react";
import {
  ArrowsClockwise,
  CheckCircle,
  Wrench,
  Image as ImageIcon,
  XCircle,
  ArrowDown,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import { toast } from "sonner";

/**
 * Iter 20.20 — Repair Live Data admin card.
 *
 * Thin UI wrapper around the existing backend endpoint
 *   POST /api/admin/integrity/live-repair
 *
 * Hard guardrails enforced server-side:
 *  - Only the 4 launch operators (Click / DCG / Elite / LLF Games).
 *  - Refuses Feel Good / BOTB / 7Days / non-launch operators.
 *  - Never auto-promotes status; closed/stale records → propose `expired`.
 *
 * Defaults per user requirements (message #761):
 *  - include_verified_live = true (the problem is on the public site)
 *  - include_source_pending_review = false (do NOT pull pending records by default)
 */
export default function LiveRepairCard({ diag, onApplied }) {
  const [operator, setOperator] = useState("");
  const [includeVerified, setIncludeVerified] = useState(true);
  const [includePending, setIncludePending] = useState(false);
  const [llmFallback, setLlmFallback] = useState(true);
  const [llmCap, setLlmCap] = useState(50);
  const [limit, setLimit] = useState(100);
  const [busy, setBusy] = useState(null);
  const [plan, setPlan] = useState(null);

  const runDryRun = async () => {
    setBusy("dry-run");
    setPlan(null);
    try {
      const { data } = await api.post("/admin/integrity/live-repair", {
        dry_run: true,
        operator: operator || null,
        include_verified_live: includeVerified,
        include_source_pending_review: includePending,
        limit,
        llm_fallback: llmFallback,
        llm_call_cap: llmCap,
      });
      setPlan(data);
      const s = data.summary || {};
      toast.success(
        `Live Repair dry-run: ${s.scanned || 0} scanned · ` +
          `${s.max_tickets_newly_found || 0} new odds · ` +
          `${s.marked_expired || 0} proposed expired · ` +
          `${s.still_unknown || 0} honestly unknown.`,
      );
    } catch (e) {
      toast.error(
        "Live Repair dry-run failed: " +
          (e.response?.data?.detail?.error ||
            JSON.stringify(e.response?.data?.detail) ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    if (!plan?.live_repair_plan_id) return;
    const s = plan.summary || {};
    const total =
      (s.max_tickets_newly_found || 0) +
      (s.marked_expired || 0) +
      (plan.records_count || 0);
    if (total === 0) {
      toast("Nothing to apply — plan has no proposed changes.");
      return;
    }
    if (
      !confirmProductionWrite(
        diag,
        `Apply Live Repair: ${plan.records_count || 0} records · ` +
          `${s.max_tickets_newly_found || 0} new odds · ` +
          `${s.marked_expired || 0} → expired. ` +
          `Status preserved except for closed/stale records.`,
      )
    ) {
      return;
    }
    setBusy("apply");
    try {
      const { data } = await api.post("/admin/integrity/live-repair", {
        dry_run: false,
        live_repair_plan_id: plan.live_repair_plan_id,
      });
      toast.success(
        `Live Repair applied: ${data.updated} updated · ` +
          `${data.expired} → expired · ${data.skipped} skipped · ` +
          `${(data.errors || []).length} errors.`,
      );
      setPlan({ ...plan, last_apply: data });
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(
        "Live Repair apply failed: " +
          (e.response?.data?.detail?.error ||
            JSON.stringify(e.response?.data?.detail) ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const summary = plan?.summary || null;
  const llmDiag = plan?.llm_diag || null;
  const diagnostics = plan?.diagnostics || [];
  const sample = plan?.sample_before_after || [];
  const byOperator = summary?.by_operator || {};

  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] to-transparent p-5 space-y-4"
      data-testid="live-repair-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wrench
              size={18}
              weight="bold"
              className="text-[var(--cc-gold)]"
            />
            <h3
              className="text-lg font-semibold text-white"
              data-testid="live-repair-title"
            >
              Repair Live Data
            </h3>
          </div>
          <p className="text-xs text-white/60 max-w-2xl leading-relaxed">
            Re-fetches each operator&apos;s live listing page + detail pages,
            patches missing odds/images/dates on currently-displayed records,
            recomputes scores, and proposes <code>expired</code> for
            closed/stale records. Locked to the 4 launch operators (Click,
            DCG, Elite, LLF Games). Never auto-promotes status. Defaults
            to <strong>verified_live only</strong> — pending review is
            opt-in.
          </p>
        </div>
        <div
          className="text-[10px] px-2 py-1 rounded border border-white/10 text-white/60"
          data-testid="live-repair-launch-operators"
        >
          Allowed:{" "}
          <span className="text-[var(--cc-gold)]">
            Click · DCG · Elite · LLF Games
          </span>
        </div>
      </div>

      {/* Options */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={includeVerified}
            onChange={(e) => setIncludeVerified(e.target.checked)}
            data-testid="live-repair-include-verified"
          />
          Include <code className="text-[10px]">verified_live</code>{" "}
          <span className="text-emerald-300/70">(default ON)</span>
        </label>
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={includePending}
            onChange={(e) => setIncludePending(e.target.checked)}
            data-testid="live-repair-include-pending"
          />
          Include <code className="text-[10px]">source_pending_review</code>{" "}
          <span className="text-amber-300/70">(default OFF)</span>
        </label>
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={llmFallback}
            onChange={(e) => setLlmFallback(e.target.checked)}
            data-testid="live-repair-llm-toggle"
          />
          LLM fallback (gpt-4o-mini)
        </label>
        <label className="text-white/80">
          Operator:
          <select
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            data-testid="live-repair-operator-select"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs"
          >
            <option value="">All launch operators</option>
            <option value="Click Competitions">Click Competitions</option>
            <option value="Dream Car Giveaways">Dream Car Giveaways</option>
            <option value="Elite Competitions">Elite Competitions</option>
            <option value="LLF Games">LLF Games</option>
          </select>
        </label>
        <label className="text-white/80">
          Limit:
          <input
            type="number"
            value={limit}
            min={1}
            max={500}
            onChange={(e) => setLimit(parseInt(e.target.value || "100", 10))}
            data-testid="live-repair-limit-input"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs w-20"
          />
        </label>
        <label className="text-white/80">
          LLM cap:
          <input
            type="number"
            value={llmCap}
            min={0}
            max={200}
            onChange={(e) => setLlmCap(parseInt(e.target.value || "50", 10))}
            data-testid="live-repair-llm-cap-input"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs w-20"
          />
        </label>
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={runDryRun}
          disabled={busy != null}
          data-testid="live-repair-dry-run-btn"
          className="btn-gold px-4 py-2 rounded text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" />
          {busy === "dry-run" ? "Running dry-run…" : "Dry-run Live Repair"}
        </button>
        <button
          onClick={runApply}
          disabled={busy != null || !plan?.live_repair_plan_id}
          data-testid="live-repair-apply-btn"
          className="px-4 py-2 rounded text-xs inline-flex items-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CheckCircle size={14} weight="bold" />
          {busy === "apply" ? "Applying…" : "Apply Live Repair"}
        </button>
        {plan?.live_repair_plan_id && (
          <span
            className="text-[10px] text-white/50 font-mono"
            data-testid="live-repair-plan-id"
          >
            plan {plan.live_repair_plan_id.slice(0, 8)}
            {plan.plan_expires_at
              ? ` · expires ${new Date(plan.plan_expires_at).toLocaleTimeString("en-GB")}`
              : ""}
          </span>
        )}
      </div>

      {/* Summary tiles */}
      {summary && (
        <div
          className="rounded-md bg-black/30 border border-white/10 p-3 space-y-3"
          data-testid="live-repair-summary"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <Stat
              label="Live records scanned"
              value={summary.scanned}
              testId="live-repair-scanned"
            />
            <Stat
              label="Already complete"
              value={summary.already_complete}
              testId="live-repair-already-complete"
              tone="muted"
            />
            <Stat
              label="Odds newly found"
              value={summary.max_tickets_newly_found}
              testId="live-repair-odds-found"
              tone="ok"
            />
            <Stat
              label="Records to update"
              value={plan?.records_count || 0}
              testId="live-repair-records-count"
              tone="ok"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
            <Stat
              label="Still unknown (with reason)"
              value={summary.still_unknown}
              testId="live-repair-still-unknown"
              tone={summary.still_unknown ? "warn" : "muted"}
            />
            <Stat
              label="Proposed expired"
              value={summary.marked_expired}
              testId="live-repair-marked-expired"
              tone={summary.marked_expired ? "warn" : "muted"}
            />
            <Stat
              label="Fetch failures"
              value={summary.fetch_failed}
              testId="live-repair-fetch-failed"
              tone={summary.fetch_failed ? "bad" : "muted"}
            />
            <Stat
              label="Sanitiser rejections"
              value={summary.sanitiser_rejected}
              testId="live-repair-sanitiser-rejected"
              tone={summary.sanitiser_rejected ? "warn" : "muted"}
            />
          </div>

          {/* By-operator breakdown */}
          {Object.keys(byOperator).length > 0 && (
            <div
              className="border-t border-white/10 pt-2"
              data-testid="live-repair-by-operator"
            >
              <div className="text-[10px] uppercase tracking-wide text-white/50 mb-1">
                By operator
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[10px]">
                {Object.entries(byOperator).map(([op, c]) => (
                  <div
                    key={op}
                    className="flex items-center justify-between bg-white/[0.03] rounded px-2 py-1"
                    data-testid={`live-repair-op-${op.replace(/\s+/g, "-")}`}
                  >
                    <span className="text-white/80 font-medium truncate mr-2">
                      {op}
                    </span>
                    <span className="text-white/60 font-mono-num text-[9px]">
                      scanned={c.scanned || 0} · found=
                      <span className="text-emerald-300">
                        {c.max_tickets_newly_found || 0}
                      </span>{" "}
                      · unknown={c.still_unknown || 0} · expire=
                      <span className="text-amber-200">
                        {c.marked_expired || 0}
                      </span>{" "}
                      · cards={c.listing_cards_seen || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* LLM diagnostics */}
      {llmDiag && (
        <div
          className="rounded-md bg-purple-500/5 border border-purple-500/20 p-3 text-[11px] text-white/70 space-y-1"
          data-testid="live-repair-llm-diag"
        >
          <div className="font-semibold text-white/80">
            LLM diagnostics ·{" "}
            <span className="text-[var(--cc-gold)]">
              {llmDiag.provider_resolved?.provider}
            </span>{" "}
            · {llmDiag.provider_resolved?.model}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
            <div>
              Calls: <strong>{llmDiag.llm_calls_attempted}</strong> attempted ·
              {llmDiag.llm_calls_succeeded} ok ·{" "}
              {llmDiag.llm_calls_failed} failed
            </div>
            <div>
              Cap skips: {llmDiag.llm_calls_skipped_cap} · no-key skips:{" "}
              {llmDiag.llm_calls_skipped_no_key}
            </div>
            <div>Cache hits: {llmDiag.llm_cache_hits}</div>
            <div data-testid="live-repair-llm-cost">
              Est. cost:{" "}
              <strong>${llmDiag.llm_estimated_cost_usd.toFixed(6)}</strong>
            </div>
            {(llmDiag.llm_calls_rate_limited > 0
              || llmDiag.llm_calls_skipped_rate_limit > 0
              || llmDiag.records_left_unknown_due_to_rate_limit > 0) && (
              <div
                className="col-span-3 text-amber-200 text-[10px]"
                data-testid="live-repair-rate-limit-row"
              >
                ⚠ Rate-limited: {llmDiag.llm_calls_rate_limited || 0} calls hit 429 ·{" "}
                {llmDiag.llm_calls_skipped_rate_limit || 0} skipped after first 429 ·{" "}
                {llmDiag.records_left_unknown_due_to_rate_limit || 0} records left
                unknown due to rate limit
              </div>
            )}
            {llmDiag.llm_disabled_reason && (
              <div className="col-span-3 text-amber-200">
                Disabled: <code>{llmDiag.llm_disabled_reason}</code>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sample before/after */}
      {sample.length > 0 && (
        <details
          className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-3"
          data-testid="live-repair-sample"
          open
        >
          <summary className="cursor-pointer text-xs font-semibold text-white/80">
            Sample · before vs after ({sample.length})
          </summary>
          <div className="mt-2 space-y-2 text-[11px]">
            {sample.map((s) => (
              <div
                key={s.id}
                className="border-l-2 border-emerald-500/40 pl-2"
                data-testid={`live-repair-sample-${s.id}`}
              >
                <div className="text-white/85 font-medium truncate">
                  {s.title}{" "}
                  <span className="text-[10px] text-white/50">
                    · {s.operator}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-1 mt-0.5 text-white/65">
                  <div>
                    <span className="opacity-60">before:</span> max=
                    <strong>{s.before.max_tickets ?? "—"}</strong> · odds=
                    <strong>{s.before.estimated_odds}</strong> · score=
                    <strong>{s.before.overall_compcheck_score}</strong>
                  </div>
                  <div>
                    <span className="opacity-60">after:</span> max=
                    <strong className="text-emerald-300">
                      {s.after.max_tickets ?? "—"}
                    </strong>{" "}
                    · odds=
                    <strong className="text-emerald-300">
                      {s.after.estimated_odds}
                    </strong>{" "}
                    · score=
                    <strong className="text-emerald-300">
                      {s.after.overall_compcheck_score}
                    </strong>
                  </div>
                </div>
                <div className="text-[10px] text-white/50 mt-0.5 flex items-center gap-3">
                  <span>action: <code>{s.final_action}</code></span>
                  {s.after.image_source_layer && (
                    <span className="inline-flex items-center gap-1">
                      <ImageIcon size={10} /> {s.after.image_source_layer}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Diagnostics table */}
      {diagnostics.length > 0 && (
        <details
          className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2"
          data-testid="live-repair-diagnostics"
        >
          <summary className="cursor-pointer text-xs font-semibold text-white/80">
            All diagnostics ({diagnostics.length})
          </summary>
          <div className="mt-2 max-h-80 overflow-auto space-y-1 text-[10px]">
            {diagnostics.map((diagItem) => (
              <div
                key={diagItem.record_id}
                className="flex items-start gap-2 py-1 border-b border-white/5"
                data-testid={`live-repair-diag-${diagItem.record_id}`}
              >
                <ActionIcon action={diagItem.final_action} />
                <div className="flex-1 min-w-0">
                  <div className="text-white/80 truncate font-medium">
                    {diagItem.title}
                  </div>
                  <div className="text-white/50 truncate">
                    <code className="text-[9px]">
                      {diagItem.final_action}
                    </code>
                    {diagItem.extracted_max_tickets ? (
                      <>
                        {" · max → "}
                        <strong className="text-emerald-300">
                          {diagItem.extracted_max_tickets}
                        </strong>
                      </>
                    ) : null}
                    {diagItem.reason_if_missing ? (
                      <>
                        {" · reason: "}
                        <code className="text-amber-200 text-[9px]">
                          {diagItem.reason_if_missing}
                        </code>
                      </>
                    ) : null}
                    <span className="text-white/30">
                      {" "}
                      · {diagItem.operator}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {plan?.last_apply && (
        <div
          className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100"
          data-testid="live-repair-last-apply"
        >
          Applied at{" "}
          {new Date(plan.last_apply.applied_at).toLocaleString("en-GB")}:{" "}
          <strong>{plan.last_apply.updated}</strong> updated ·{" "}
          <strong>{plan.last_apply.expired}</strong> → expired ·{" "}
          {plan.last_apply.skipped} skipped ·{" "}
          {(plan.last_apply.errors || []).length} errors.
        </div>
      )}
    </div>
  );
}

function ActionIcon({ action }) {
  if (action === "updated_odds" || action === "updated_partial") {
    return (
      <CheckCircle
        size={12}
        weight="bold"
        className="text-emerald-300 mt-0.5 shrink-0"
      />
    );
  }
  if (action === "expired_closed_detected") {
    return (
      <ArrowDown
        size={12}
        weight="bold"
        className="text-amber-300 mt-0.5 shrink-0"
      />
    );
  }
  if (action === "fetch_failed") {
    return (
      <XCircle
        size={12}
        weight="bold"
        className="text-red-300 mt-0.5 shrink-0"
      />
    );
  }
  return (
    <ArrowsClockwise
      size={12}
      weight="bold"
      className="text-white/40 mt-0.5 shrink-0"
    />
  );
}

function Stat({ label, value, testId, tone }) {
  const toneCls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200"
        : tone === "bad"
          ? "bg-red-500/15 text-red-200"
          : tone === "muted"
            ? "bg-white/5 text-white/60"
            : "bg-white/5 text-white/80";
  return (
    <div className={`rounded px-2 py-1.5 ${toneCls}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide opacity-70">
        {label}
      </div>
      <div className="text-sm font-semibold font-mono-num">{value ?? 0}</div>
    </div>
  );
}
